import { StockChartDataPoint } from '@/types/stock-data'

/**
 * Utility functions for transforming and sampling stock chart data
 */

/**
 * Helper function to determine the best sampling period based on actual data span
 * This is used for data that may be shorter than expected (e.g., newly listed companies)
 */
export function getBestSamplingPeriod(data: StockChartDataPoint[]): string {
    if (data.length === 0) return 'W'

    const firstDataPoint = data[0]
    const lastDataPoint = data[data.length - 1]

    if (!firstDataPoint || !lastDataPoint) return 'W'

    const firstDate = new Date(firstDataPoint.trade_date)
    const lastDate = new Date(lastDataPoint.trade_date)
    const daysDiff = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)

    // Return the appropriate period based on actual data span
    if (daysDiff <= 7) return 'W'
    if (daysDiff <= 30) return 'M'
    if (daysDiff <= 90) return '3M'
    if (daysDiff <= 365) return 'Y'
    if (daysDiff <= 5 * 365) return '5Y'
    if (daysDiff <= 10 * 365) return '10Y'
    return 'ALL'
}

/**
 * Sample data points based on the period to optimize chart rendering
 * Reduces the number of data points while preserving the overall shape
 */
export function sampleDataPoints(
    data: StockChartDataPoint[],
    requestedPeriod: string,
    actualDataPeriod: string
): StockChartDataPoint[] {
    if (data.length === 0) return data

    // Use the actual data period for sampling, not the requested period
    // This ensures consistent intervals based on how long the company has existed
    const samplingPeriod = actualDataPeriod

    switch (samplingPeriod) {
        case 'Y':
            // For 1 year of data, sample every 2nd point
            return data.filter((_, index) => index % 2 === 0 || index === data.length - 1)

        case '5Y':
            // For 5 years of data, sample every 7th point
            return data.filter((_, index) => index % 7 === 0 || index === data.length - 1)

        case '10Y':
            // For 10+ years of data, sample every 15th point
            return data.filter((_, index) => index % 15 === 0 || index === data.length - 1)

        case 'ALL':
            // For ALL data (very long periods), sample every 40th point
            return data.filter((_, index) => index % 40 === 0 || index === data.length - 1)

        default:
            // For shorter periods (W, M, 3M), use all data points
            return data
    }
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
    return `$${price.toFixed(2)}`
}

/**
 * Format volume for display
 */
export function formatVolume(volume: number): string {
    if (volume >= 1000000) {
        return `${(volume / 1000000).toFixed(1)}M`
    }
    if (volume >= 1000) {
        return `${(volume / 1000).toFixed(1)}K`
    }
    return volume.toString()
}

/**
 * Calculate percentage change between two prices
 */
export function calculatePercentageChange(currentPrice: number, previousPrice: number): number {
    if (previousPrice === 0) return 0
    return ((currentPrice - previousPrice) / previousPrice) * 100
}

/**
 * Get price change info from data array
 */
export function getPriceChangeInfo(data: StockChartDataPoint[]): {
    currentPrice: number
    percentageChange: number
    isPositive: boolean
} {
    if (!data || data.length === 0) {
        return { currentPrice: 0, percentageChange: 0, isPositive: true }
    }

    const lastDataPoint = data[data.length - 1]
    const firstDataPoint = data[0]

    if (!lastDataPoint || !firstDataPoint) {
        return { currentPrice: 0, percentageChange: 0, isPositive: true }
    }

    const currentPrice = lastDataPoint.close
    const firstPrice = firstDataPoint.close
    const percentageChange = calculatePercentageChange(currentPrice, firstPrice)
    const isPositive = percentageChange >= 0

    return {
        currentPrice,
        percentageChange: Math.abs(percentageChange),
        isPositive,
    }
}

/**
 * Map old timeframe labels to new TimeframeType
 * Helper for migration from old format to new format
 */
export function mapLegacyTimeframeToNew(
    legacyTimeframe: string
): '1D' | 'W' | 'M' | '3M' | '1Y' | '5Y' | '10Y' | 'ALL' {
    const mapping: Record<string, '1D' | 'W' | 'M' | '3M' | '1Y' | '5Y' | '10Y' | 'ALL'> = {
        '1D': '1D',
        '1W': 'W',
        W: 'W',
        '1M': 'M',
        M: 'M',
        '3M': '3M',
        '6M': '3M', // Map 6M to 3M (closest match)
        '1Y': '1Y',
        Y: '1Y',
        '5Y': '5Y',
        '10Y': '10Y',
        '20Y': 'ALL', // Map 20Y to ALL
        '40Y': 'ALL', // Map 40Y to ALL
        ALL: 'ALL',
    }

    return mapping[legacyTimeframe] || 'M'
}
